import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMemo, useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { ColumnDef } from "@tanstack/react-table";
import { OrderDocumentType } from "@/lib/validations/schema";
import { DataTableColumnHeader } from "components/Table/data-table-column-header";
import { DataTable } from "components/Table/DataTable";
import { DocumentItem, Customers as TypeCustomer } from "pages/GenericOrderDetailsPage/types/order";
import DocumentReview from "pages/OrderDetails/verifydocuments";
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "react-query";
import { approveDocuments, requestDocument, reuploadDocument } from "pages/GenericOrderDetailsPage/apis";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { Spinner } from "components";
import RequestOrderDoc from "pages/OrderDetails/request_doc";
import EditProduct from "pages/GenericOrderDetailsPage/components/edit-product-form";
import { convertFileToBase64 } from "pages/CreateRemit/documentation";
import { all_doc_List } from "pages/GenericOrderDetailsPage/utils";

const tabsdata = [
    {
        id: 'documents',
        label: 'Documents',
        value: 'documents',
        enabled: true
    },
    {
        id: 'lrs',
        label: 'LRS',
        value: 'lrs',
        enabled: false
    }
];

export default function Customers({ customers, order_documents, selectedCustomerId, onCustomerTabClick, documentsButtonState, paymentButtonState }: {
    customers: TypeCustomer;
    selectedCustomerId?: number | null;
    onCustomerTabClick?: (customerId: number) => void;
    order_documents: DocumentItem[];
    documentsButtonState: {
        disabled: boolean;
        visible: boolean;
    },
    paymentButtonState: {
        disabled: boolean;
        visible: boolean;
    }
}) {
    const [activeTab, setActiveTab] = useState<string>('');
    const [open, setOpen] = useState(false);
    const orderId = useParams().id as string;
    const queryClient = useQueryClient();
    const [activeCustomer, setActiveCustomer] = useState<any>(null)

    const orderDocumentsToPassInReviewModal = useMemo(() => {
        return order_documents
            ?.filter((doc) => doc?.created_by_cx === activeCustomer)
            .map((doc) => ({
                id: doc.id as string,
                name: doc.doc_name as string,
                type: doc.document?.type as 'pdf' | 'image',
                status: doc.status as "Unverified" | "Verified" | "Requested",
                url: doc.document?.url as string
            }));
    }, [order_documents, activeCustomer]);

    // group by customer name
    const groupedTabsData: Record<string, any> = customers?.product_customer?.reduce((acc: any, tab) => {
        if (tab?.status === 'Reject') return acc;
        const customerName = tab.customer_detail?.full_name;
        if (!acc[customerName]) {
            acc[customerName] = {
                id: tab.id,
                label: customerName,
                value: customerName,
                data: [tab],
                enabled: true,
                customerId: tab.created_for,
            };
        } else {
            acc[customerName] = {
                ...acc[customerName],
                data: [...acc[customerName].data, tab],
            };
        }
        return acc;
    }, {} as Record<string, any>);
    const tabsdata: any[] = Object.values(groupedTabsData);

    // set active tab as first tab from tabData or selectedCustomerId
    useEffect(() => {
        if (selectedCustomerId) {
            const found = tabsdata.find((tab: any) => tab.customerId === selectedCustomerId);

            if (found) {
                setActiveTab(found.value);
                setActiveCustomer(found?.customerId)
            }
        } else if (activeTab === '' && tabsdata.length > 0) {
            setActiveTab((tabsdata[0] as any)?.value);
            setActiveCustomer(tabsdata[0]?.customerId)
        }
    }, [selectedCustomerId, tabsdata]);

    const onTabChange = (value: string) => {
        setActiveTab(value);
    };
    const { mutate, isLoading } = useMutation({
        mutationFn: approveDocuments,
        onSuccess: (data) => {
            toast.success('Documents approved successfully');
            queryClient.invalidateQueries({ queryKey: ['orderDetails'] });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error approving documents');
        }
    })

    const { mutate: reuploadMutation, isLoading: reuploadLoading } = useMutation({
        mutationFn: reuploadDocument,
        onSuccess: (data) => {
            toast.success('Documents approved successfully');
            queryClient.invalidateQueries({ queryKey: ['orderDetails'] });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error approving documents');
        }
    })

    const onApprove = async (id: string, name: string, file: File | null) => {
        let base64: Base64URLString;
        const matchedDoc = all_doc_List?.find(doc => String(doc.id) === id);
        const type = matchedDoc?.type || "personal";

        if (file) {
            base64 = await convertFileToBase64(file) as Base64URLString
            reuploadMutation({
                documents_id: id,
                doc: base64,
                order_details_id: Number(orderId),
                customers_id: activeCustomer,
                type: type,
            });
        } else {
            mutate({
                order_details_id: orderId,
                document_list: [
                    {
                        id: id,
                        status: 'verified'
                    }
                ]
            })
        }
    };
    const onReUpload = async (id: string, file: string, name: string) => {
        if (file) {
            reuploadMutation({
                documents_id: id,
                doc: file,
                order_details_id: Number(orderId)
            })
        }
    }


    const { mutate:requestDocMutation, isLoading:RequestDocLoading } = useMutation({
        mutationFn: requestDocument,
        onSuccess: () => {
            queryClient.invalidateQueries(['orderDetails'])
            toast.success('Document requested successfully');
        },
        onError: () => { }
    })

    const handleRequestDocument = (id: number) => {
        const matchedDoc = all_doc_List?.find(doc => doc.id === id);
        const type = matchedDoc?.type || 'personal';

        requestDocMutation({
            order_id: Number(orderId),
            req_doc: [
                {
                    doc_list_id: id,
                    type: type,
                    req_for: Number(activeCustomer),
                }
            ]
        });
    };

    return (
        <>
            {
                open && (
                    <DocumentReview
                        isOpen={open}
                        setIsOpen={setOpen}
                        onReUpload={onReUpload}
                        refetchOrderDetails={() => { }}
                        documents={orderDocumentsToPassInReviewModal}
                        onApprove={onApprove}
                        onRequest={handleRequestDocument}
                    />
                )
            }
            {
                (isLoading || reuploadLoading || RequestDocLoading) && <Spinner />
            }

            <Tabs value={activeTab} onValueChange={onTabChange} defaultValue="customers" className="h-full flex gap-8">
                <TabsList className="flex items-start w-fit h-full flex-col rounded-xl py-6 px-3 border border-[#DDDDDD] gap-2 bg-[#FBFBFB]">
                    {tabsdata?.map((tab: any) => {
                        return (
                            <TabsTrigger
                                key={tab.id}
                                value={tab.value}
                                disabled={!tab.enabled}
                                onClick={() => { onCustomerTabClick && onCustomerTabClick(tab.customerId), setActiveCustomer(tab.customerId) }}
                                className={`px-3 py-3.5 border text-black-label capitalize border-transparent w-[215px] text-sm ${tab.value === activeTab &&
                                    " rounded-lg  border-blue-700 text-blue-700 shadow bg-[#EFF4FF]"
                                    }`}
                            >
                                <div className="flex gap-2 items-center justify-between w-full">
                                    {tab.label}
                                    <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8 0.5C3.592 0.5 0 4.092 0 8.5C0 12.908 3.592 16.5 8 16.5C12.408 16.5 16 12.908 16 8.5C16 4.092 12.408 0.5 8 0.5ZM11.824 6.66L7.288 11.196C7.176 11.308 7.024 11.372 6.864 11.372C6.704 11.372 6.552 11.308 6.44 11.196L4.176 8.932C3.944 8.7 3.944 8.316 4.176 8.084C4.408 7.852 4.792 7.852 5.024 8.084L6.864 9.924L10.976 5.812C11.208 5.58 11.592 5.58 11.824 5.812C12.056 6.044 12.056 6.42 11.824 6.66Z" fill="#D2D2D2" />
                                    </svg>

                                </div>
                            </TabsTrigger>
                        );
                    })}
                </TabsList>
                {tabsdata?.map((tab: any) => {
                    // filter out empty array from and array
                    // const filteredData = tab?.data?.[0].customer_detail?.documents_id?.filter((item: any) => {
                    //     if (Array.isArray(item)) {
                    //         return item.length > 0;
                    //     }
                    //     return item !== null && item !== undefined;
                    // });
                    // combine order_documents and filteredData array
                    let combinedData: any[] = []
                    // if (filteredData?.length)
                    //     combinedData = [...order_documents, ...filteredData];
                    // else
                    combinedData = [...order_documents];

                    // group document by type_of_doc
                    let groupedDocuments = combinedData?.reduce((acc: any, doc: any) => {
                        const docType = doc.type_of_doc || 'others';
                        if (!acc[docType]) {
                            acc[docType] = [];
                        }
                        acc[docType].push(doc);
                        return acc;
                    }, {} as Record<string, any>);
                    // add an all tab
                    const allDocuments = Object.values(groupedDocuments).flat();
                    groupedDocuments['all'] = allDocuments;
                    // move all tab to first
                    const allDocumentsData = groupedDocuments['all'];
                    delete groupedDocuments['all'];
                    groupedDocuments = {
                        'all': allDocumentsData,
                        ...groupedDocuments
                    };

                    return (
                        <TabsContent
                            key={tab.id}
                            value={tab.value}
                            className="h-full w-full space-y-6 m-0"
                        >
                            <CustomerSection
                                name={tab?.data[0]?.customer_detail?.full_name}
                                phone={tab?.data[0]?.customer_detail?.phone_number}
                                email={tab?.data[0]?.customer_detail?.email}
                            />
                            <div className="grid grid-cols-2 w-4/5 gap-4">                                
                                {tab?.data.map((product: any) => {
                                    return (
                                        <>
                                            <ProductCard
                                                isDisabled={paymentButtonState?.disabled}
                                                key={product.name}
                                                product={product?.name}
                                                qty={product.quantity}
                                                currency={product?.country_currency_want}
                                                id={product?.id}
                                                rate={product?.rate_and_margins?.[0]?.ibr_rate || 0}
                                            />
                                        </>
                                    );
                                })}
                            </div>
                            <DocumentsAndLRS documentsButtonState={documentsButtonState} setOpen={setOpen} open={open} lrs={[]} documents={groupedDocuments} activeCustomer={activeCustomer} />
                        </TabsContent>
                    );
                })}
            </Tabs>
        </>
    )
}

const CustomerSection = ({ name, phone, email }: {
    name: string;
    phone: string;
    email: string;
}) => {
    return (
        <div className="rounded-xl bg-[#F8FAFF] border border-[#DDE9FF] py-4 px-8">
            <div className="flex justify-between flex-col gap-3">
                <h2 className="text-xl font-semibold leading-[150%] text-blue-700 capitalize">{name}</h2>
                <div className="flex gap-[22px] items-center">
                    <small className="flex gap-3 items-center text-sm font-normal leading-[160%] text-black-label">
                        <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.2219 12.0429L12.5639 11.7031L11.7696 10.9051L11.4291 11.2449L12.2219 12.0429ZM13.7106 11.5606L15.1439 12.3399L15.6801 11.3514L14.2476 10.5729L13.7106 11.5606ZM15.4191 13.8879L14.3541 14.9476L15.1469 15.7449L16.2119 14.6859L15.4191 13.8879ZM13.7046 15.2881C12.6171 15.3901 9.80463 15.2994 6.75813 12.2709L5.96463 13.0681C9.28863 16.3734 12.4529 16.5354 13.8096 16.4086L13.7046 15.2881ZM6.75813 12.2709C3.85488 9.38335 3.37338 6.9556 3.31338 5.90185L2.18988 5.9656C2.26488 7.2916 2.86113 9.9826 5.96463 13.0681L6.75813 12.2709ZM7.78938 7.63585L8.00463 7.42135L7.21263 6.6241L6.99738 6.83785L7.78938 7.63585ZM8.17563 4.6951L7.23063 3.4321L6.32988 4.1071L7.27488 5.36935L8.17563 4.6951ZM4.04988 3.15685L2.87238 4.32685L3.66588 5.12485L4.84263 3.95485L4.04988 3.15685ZM7.39338 7.23685C6.99588 6.83785 6.99588 6.83785 6.99588 6.83935H6.99438L6.99213 6.84235C6.95649 6.87829 6.92458 6.91774 6.89688 6.9601C6.85638 7.0201 6.81213 7.09885 6.77463 7.1986C6.68334 7.4561 6.66059 7.73291 6.70863 8.00185C6.80913 8.6506 7.25613 9.50785 8.40063 10.6464L9.19413 9.84835C8.12238 8.78335 7.86738 8.13535 7.82013 7.82935C7.79763 7.68385 7.82088 7.61185 7.82763 7.59535C7.83213 7.58535 7.83213 7.58385 7.82763 7.59085C7.82094 7.6012 7.81342 7.61099 7.80513 7.6201L7.79763 7.6276L7.79013 7.63435L7.39338 7.23685ZM8.40063 10.6464C9.54588 11.7849 10.4076 12.2289 11.0571 12.3279C11.3894 12.3789 11.6571 12.3384 11.8604 12.2626C11.9742 12.221 12.0806 12.1612 12.1754 12.0856L12.2129 12.0519L12.2181 12.0474L12.2204 12.0451L12.2211 12.0436C12.2211 12.0436 12.2219 12.0429 11.8251 11.6439C11.4276 11.2449 11.4299 11.2441 11.4299 11.2441L11.4314 11.2426L11.4329 11.2411L11.4374 11.2374L11.4449 11.2299L11.4734 11.2074C11.4804 11.2029 11.4786 11.2034 11.4681 11.2089C11.4494 11.2156 11.3759 11.2389 11.2281 11.2164C10.9176 11.1684 10.2651 10.9134 9.19413 9.84835L8.40063 10.6464ZM7.23063 3.43135C6.46563 2.41135 4.96263 2.24935 4.04988 3.15685L4.84263 3.95485C5.24163 3.5581 5.94963 3.59935 6.32988 4.1071L7.23063 3.43135ZM3.31413 5.9026C3.29913 5.6431 3.41838 5.3716 3.66588 5.1256L2.87163 4.3276C2.46888 4.7281 2.15163 5.2951 2.18988 5.9656L3.31413 5.9026ZM14.3541 14.9476C14.1486 15.1531 13.9266 15.2686 13.7054 15.2889L13.8096 16.4086C14.3609 16.3569 14.8116 16.0794 15.1476 15.7456L14.3541 14.9476ZM8.00463 7.42135C8.74338 6.6871 8.79813 5.52685 8.17638 4.69585L7.27563 5.3701C7.57788 5.77435 7.53288 6.3046 7.21188 6.62485L8.00463 7.42135ZM15.1446 12.3406C15.7574 12.6736 15.8526 13.4581 15.4199 13.8886L16.2134 14.6859C17.2184 13.6861 16.9086 12.0189 15.6809 11.3521L15.1446 12.3406ZM12.5639 11.7039C12.8519 11.4174 13.3154 11.3469 13.7114 11.5614L14.2484 10.5736C13.4354 10.1311 12.4274 10.2534 11.7704 10.9059L12.5639 11.7039Z" fill="#9E9E9E" />
                        </svg>

                        {phone || 'N/A'}
                    </small>
                    <small className="flex gap-3 items-center text-sm font-normal leading-[160%] text-black-label">
                        <svg width="15" height="13" viewBox="0 0 15 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 2C15 1.175 14.325 0.5 13.5 0.5H1.5C0.675 0.5 0 1.175 0 2V11C0 11.825 0.675 12.5 1.5 12.5H13.5C14.325 12.5 15 11.825 15 11V2ZM13.5 2L7.5 5.7425L1.5 2H13.5ZM13.5 11H1.5V3.5L7.5 7.25L13.5 3.5V11Z" fill="#9E9E9E" />
                        </svg>
                        {email || 'N/A'}
                    </small>
                </div>
            </div>
        </div>
    )
}

const ProductCard = ({ id, product, qty, currency, isDisabled, rate}: {
    product: string;
    qty: number;
    currency: string;
    id: string
    rate: number;
    isDisabled: boolean;
}) => {
    return (
        <Card className="p-4 w-full border rounded-xl relative">
            <div className="flex justify-between w-full">
                <div className="flex flex-col gap-3 w-full">
                    <div className="text-sm text-gray-700 w-full font-medium flex items-center justify-between">
                        <p className="">{product}</p>
                        <button disabled={isDisabled} className="disabled:opacity-40 right-4 text-blue-600">
                            <EditProduct quantity={qty} product_id={id}>
                                <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.25781 4.53906H2.42448C1.98245 4.53906 1.55853 4.71466 1.24597 5.02722C0.933407 5.33978 0.757813 5.7637 0.757812 6.20573V13.7057C0.757813 14.1478 0.933407 14.5717 1.24597 14.8842C1.55853 15.1968 1.98245 15.3724 2.42448 15.3724H9.92448C10.3665 15.3724 10.7904 15.1968 11.103 14.8842C11.4156 14.5717 11.5911 14.1478 11.5911 13.7057V12.8724" stroke="#165FE3" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M10.7552 2.87769L13.2552 5.37769M14.4094 4.19853C14.7376 3.87032 14.922 3.42518 14.922 2.96103C14.922 2.49687 14.7376 2.05173 14.4094 1.72353C14.0812 1.39532 13.636 1.21094 13.1719 1.21094C12.7077 1.21094 12.2626 1.39532 11.9344 1.72353L4.92188 8.71103V11.211H7.42188L14.4094 4.19853Z" stroke="#165FE3" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </EditProduct>
                        </button>
                    </div>
                    <div className="text-xs text-gray-600 flex items-center gap-1">
                        <p className="uppercase">
                            {currency}
                        </p>
                        <p className="">
                            {qty}
                        </p>
                        <p className="">
                           <span className="text-blue-700">@</span> <small className="text-gray-500">INR</small> {rate}
                        </p>
                    </div>
                </div>
            </div>
        </Card>
    )
}

const DocumentsAndLRS = ({ documents, lrs, setOpen, open, documentsButtonState, activeCustomer }: {
    documents: any;
    lrs: any;
    open: boolean;
    setOpen: (open: boolean) => void;
    documentsButtonState: {
        disabled: boolean;
        visible: boolean;
    },
    activeCustomer: any
}) => {
    const [activeTab, setActiveTab] = useState<string>('documents');
    const [documentActiveTab, setDocumentActiveTab] = useState<string>('all');
    // set active tab as first tab from tabData
    const onTabChange = (value: string) => {
        setActiveTab(value);
    };

    const documentsColumns: ColumnDef<OrderDocumentType>[] = [
        {
            id: "doc_name",
            accessorKey: "doc_name",
            header: ({ column }) => (
                <DataTableColumnHeader className="whitespace-nowrap" column={column} title="Document name" />
            ),
            cell: ({ row }) => {
                return (
                    <div className="flex items-center justify-between gap-1.5">
                        <p className="max-w-[200px] whitespace-normal break-words">
                            {row.getValue("doc_name")}
                        </p>
                    </div>
                );
            },
            enableSorting: false,
        },
        // {
        //     id: 'product',
        //     accessorKey: 'product',
        //     header: ({ column }) => (
        //         <DataTableColumnHeader column={column} title="Product" />
        //     ),
        //     cell: ({ row }) => {
        //         return (
        //             <div className="flex items-center justify-between gap-1.5">
        //                 <p className="max-w-[200px] whitespace-normal break-words">
        //                     {row.getValue("product")}
        //                 </p>
        //             </div>
        //         );
        //     },
        //     enableSorting: false,
        // },
        {
            accessorKey: 'upload_date',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Upload date" />
            ),
            cell: ({ row }) => {
                return (
                    <div className="flex items-center justify-between gap-1.5">
                        <p className="max-w-[200px] whitespace-normal break-words">
                            {row.getValue("upload_date")}
                        </p>
                    </div>
                );
            },
            enableSorting: false,
        },
        {
            accessorKey: 'doc_status',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Status" />
            ),
            cell: ({ row }) => {
                const status = row.getValue("doc_status");
                if (status === "verified") {
                    return (
                        <div className="flex items-center gap-1.5">
                            <p className="max-w-[200px] bg-[#E1FFE2] px-2 rounded-md text-[#00A82D] whitespace-normal break-words">
                                Approved
                            </p>
                            <button
                                onClick={() => window.open(row.original.doc_url)}
                                className=""
                            >
                                <svg
                                    width="18"
                                    height="12"
                                    viewBox="0 0 18 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M16.3221 5.28375C16.5501 5.60325 16.6641 5.76375 16.6641 6C16.6641 6.237 16.5501 6.39675 16.3221 6.71625C15.2976 8.15325 12.6808 11.25 9.16406 11.25C5.64656 11.25 3.03056 8.1525 2.00606 6.71625C1.77806 6.39675 1.66406 6.23625 1.66406 6C1.66406 5.763 1.77806 5.60325 2.00606 5.28375C3.03056 3.84675 5.64731 0.75 9.16406 0.75C12.6816 0.75 15.2976 3.8475 16.3221 5.28375Z"
                                        stroke="#165FE3"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M11.4141 6C11.4141 5.40326 11.177 4.83097 10.7551 4.40901C10.3331 3.98705 9.7608 3.75 9.16406 3.75C8.56733 3.75 7.99503 3.98705 7.57307 4.40901C7.15112 4.83097 6.91406 5.40326 6.91406 6C6.91406 6.59674 7.15112 7.16903 7.57307 7.59099C7.99503 8.01295 8.56733 8.25 9.16406 8.25C9.7608 8.25 10.3331 8.01295 10.7551 7.59099C11.177 7.16903 11.4141 6.59674 11.4141 6Z"
                                        stroke="#165FE3"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    );
                }
                if (status === "requested") {
                    return (
                        <div className="flex items-center gap-1.5">
                            <p className="max-w-[200px] bg-[#E1EBFC] px-2 rounded-md text-blue-700 whitespace-normal break-words">
                                Requested
                            </p>
                        </div>
                    );
                }
                return (
                    <Button variant='ghost' disabled={documentsButtonState.disabled} onClick={() => setOpen(true)} className=" w-full p-0 ">
                        <p className=" max-w-[200px] flex-1 flex justify-center items-center rounded-md p-2.5 border-black-label border gap-1.5">
                            Review
                        </p>
                    </Button>
                );
            },
            enableSorting: false,
        }
    ]

    const documentsColumnsMemoized = useMemo(() => documentsColumns, []);

    let documentsTabsdata = Object.keys(documents)?.map((tab, index) => {
        return {
            id: index + 1,
            label: tab,
            value: tab,
            enabled: true
        };
    });

    const tableData = documents?.[documentActiveTab]
        ?.filter((doc: any) => doc?.created_by_cx === activeCustomer)
        ?.map((doc: any) => {
            return {
                doc_name: doc?.doc_name || 'N/A',
                product: doc?.product || 'N/A',
                upload_date: doc?.upload_date || new Date().toLocaleDateString(),
                doc_status: doc?.status || 'N/A',
                doc_url: doc?.document?.url || '',
            }
        });

    const orderId = useParams().id as string;
    const queryCLient = useQueryClient();

    const { mutate, isLoading } = useMutation({
        mutationFn: requestDocument,
        onSuccess: () => {
            queryCLient.invalidateQueries(['orderDetails'])
            toast.success('Document requested successfully');
        },
        onError: () => { }
    })

    const handleRequestDocument = (id: number) => {
        const matchedDoc = all_doc_List?.find(doc => doc.id === id);
        const type = matchedDoc?.type || 'personal';

        mutate({
            order_id: Number(orderId),
            req_doc: [
                {
                    doc_list_id: id,
                    type: type,
                    req_for: Number(activeCustomer),
                }
            ]
        });
    };

    return (
        <Tabs value={activeTab} onValueChange={onTabChange} defaultValue="customers" className="h-full w-full flex flex-col gap-8">
            <TabsList className="w-full hide-scrollbar flex justify-start rounded-none gap-2 h-12">
                {tabsdata?.map((tab) => {
                    return (
                        <TabsTrigger
                            key={tab.id}
                            value={tab.value}
                            disabled={!tab.enabled}
                            className={`px-6 py-3.5 text-sm w-[230px] ${tab.value === activeTab &&
                                "h-11 rounded-none font-semibold border-b border-blue-700 text-blue-700"
                                }`}
                        >
                            {tab.label}
                        </TabsTrigger>
                    );
                })}
            </TabsList>
            <TabsContent value="documents">
                <Tabs value={documentActiveTab} onValueChange={setDocumentActiveTab} defaultValue="customers" className="h-full w-full flex flex-col gap-8">
                    <TabsList className=" hide-scrollbar w-full flex justify-start rounded-none gap-2 h-12">
                        {documentsTabsdata?.map((tab) => {
                            return (
                                <TabsTrigger
                                    key={tab.id}
                                    value={tab.value}
                                    disabled={!tab.enabled}
                                    className={`px-4 capitalize py-1 border border-[#E5E5E5] rounded-2xl text-sm text-[#999999] ${tab.value === documentActiveTab &&
                                        "font-semibold border-blue-700 text-blue-700"
                                        }`}
                                >
                                    {tab.label}
                                </TabsTrigger>
                            );
                        })}
                        <div className="flex gap-3 ml-auto">
                            <Button disabled={documentsButtonState.disabled} variant='ghost' className="border flex gap-1.5 items-center border-blue-700 text-blue-700 rounded-lg" onClick={() => setOpen(true)}>
                                Upload Doc
                                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.55469 6.33333C4.55469 4.49238 6.04707 3 7.88802 3H12.5169C12.7531 3 12.9783 3.10027 13.1363 3.27587L16.4241 6.92898C16.5619 7.082 16.638 7.28058 16.638 7.48645V15.0833C16.638 16.9243 15.1456 18.4167 13.3047 18.4167H7.88802C6.04707 18.4167 4.55469 16.9243 4.55469 15.0833V6.33333Z" fill="#165FE3" />
                                    <path d="M16.6328 8V7.48645C16.6328 7.28058 16.5566 7.082 16.4189 6.92897L13.1311 3.27587C12.9731 3.10027 12.7479 3 12.5116 3H11.6328V4.66667C11.6328 6.50762 13.1252 8 14.9661 8H16.6328Z" fill="#98BDFF" />
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M13.522 12.0802C13.2706 12.3167 12.8751 12.3047 12.6385 12.0532L10.5938 9.87983L8.54892 12.0532C8.31242 12.3047 7.91692 12.3167 7.6655 12.0802C7.41409 11.8437 7.402 11.4482 7.63859 11.1967L9.886 8.80792C10.2723 8.39733 10.9153 8.39733 11.3015 8.80792L13.549 11.1967C13.7855 11.4482 13.7734 11.8437 13.522 12.0802Z" fill="white" />
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M10.5938 16C10.2486 16 9.96875 15.7202 9.96875 15.375V9.125C9.96875 8.77983 10.2486 8.5 10.5938 8.5C10.9389 8.5 11.2188 8.77983 11.2188 9.125V15.375C11.2188 15.7202 10.9389 16 10.5938 16Z" fill="white" />
                                </svg>
                            </Button>
                            <RequestOrderDoc disabled={documentsButtonState.disabled} onSubmit={handleRequestDocument} order_id={orderId} >
                                <Button variant='ghost' className="border flex gap-1.5 items-center border-blue-700 text-blue-700 rounded-lg">
                                    Request Doc
                                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4.55469 6.33333C4.55469 4.49238 6.04707 3 7.88802 3H12.5169C12.7531 3 12.9783 3.10027 13.1363 3.27587L16.4241 6.92898C16.5619 7.082 16.638 7.28058 16.638 7.48645V15.0833C16.638 16.9243 15.1456 18.4167 13.3047 18.4167H7.88802C6.04707 18.4167 4.55469 16.9243 4.55469 15.0833V6.33333Z" fill="#165FE3" />
                                        <path d="M16.6328 8V7.48645C16.6328 7.28058 16.5566 7.082 16.4189 6.92897L13.1311 3.27587C12.9731 3.10027 12.7479 3 12.5116 3H11.6328V4.66667C11.6328 6.50762 13.1252 8 14.9661 8H16.6328Z" fill="#98BDFF" />
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M13.522 12.0802C13.2706 12.3167 12.8751 12.3047 12.6385 12.0532L10.5938 9.87983L8.54892 12.0532C8.31242 12.3047 7.91692 12.3167 7.6655 12.0802C7.41409 11.8437 7.402 11.4482 7.63859 11.1967L9.886 8.80792C10.2723 8.39733 10.9153 8.39733 11.3015 8.80792L13.549 11.1967C13.7855 11.4482 13.7734 11.8437 13.522 12.0802Z" fill="white" />
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M10.5938 16C10.2486 16 9.96875 15.7202 9.96875 15.375V9.125C9.96875 8.77983 10.2486 8.5 10.5938 8.5C10.9389 8.5 11.2188 8.77983 11.2188 9.125V15.375C11.2188 15.7202 10.9389 16 10.5938 16Z" fill="white" />
                                    </svg>
                                </Button>
                            </RequestOrderDoc>
                        </div>
                    </TabsList>
                    <DataTable
                        columns={documentsColumnsMemoized}
                        data={tableData || []}
                        isToolbarVisible={false}
                        isPaginationVisible={false}
                        columnsBackground="#EEEEEE"
                    />
                </Tabs>
            </TabsContent>
            <TabsContent value="lrs"></TabsContent>
        </Tabs>
    )
}